import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoginLogo } from '@/components/LoginCard/LoginLogo';

interface EmbeddedAuthDialogProps {
  open: boolean;
  phase: 'idle' | 'loading' | 'error';
  productName: string;
  errorMessage?: string;
  onSignIn: () => void;
  onRetry: () => void;
  onClose: () => void;
}

export function EmbeddedAuthDialog({
  open,
  phase,
  productName,
  errorMessage,
  onSignIn,
  onRetry,
  onClose,
}: EmbeddedAuthDialogProps) {
  const { t } = useTranslation('entity-portal');

  const isLoading = phase === 'loading';
  const hasError = phase === 'error';

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="embedded-auth-dialog-title"
    >
      <DialogTitle
        id="embedded-auth-dialog-title"
        sx={{
          pb: 1,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography variant="h6">{t('embedded.embeddedAuthDialogTitle')}</Typography>
          <IconButton
            aria-label={t('embedded.embeddedAuthDialogClose')}
            onClick={onClose}
            size="small"
            disabled={isLoading}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2.5}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <LoginLogo />
          </Box>

          <Stack spacing={1} textAlign="center" alignItems="center">
            <Typography variant="h5" sx={{ color: 'primary.main' }}>
              {t('embedded.embeddedAuthDialogWelcome')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('embedded.embeddedAuthDialogDescription', { product: productName })}
            </Typography>
          </Stack>

          {hasError ? (
            <Alert severity="error" role="alert">
              {errorMessage || t('embedded.embeddedAuthDialogErrorFallback')}
            </Alert>
          ) : null}

          {isLoading ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {t('embedded.embeddedAuthDialogLoading')}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1,
          justifyContent: 'space-between',
        }}
      >
        <Button onClick={onClose} disabled={isLoading}>
          {t('embedded.embeddedAuthDialogCancel')}
        </Button>

        <Button
          variant="contained"
          onClick={hasError ? onRetry : onSignIn}
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <LockOutlinedIcon />
            )
          }
          sx={{
            textTransform: 'none',
          }}
        >
          {hasError
            ? t('embedded.embeddedAuthDialogRetry')
            : t('embedded.embeddedAuthDialogSignInButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
